/**
 * Clips keep the seal sharp when the landing hero enlarges it. WebKit rasterizes SVG masks at their unscaled size.
 * Each clip has one plain child so WebKit can use geometry instead of a bitmap buffer.
 * Reveal clocks animate the invisible clip shapes. Opacity clocks on visible groups blur scaled SVG art in Chrome.
 */
import {drive} from '../../modules/motion/drive.js';
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {createRun} from '../../modules/motion/run.js';
import {settle} from '../../modules/motion/settle.js';

const SELECTOR = Object.freeze({
    host: '.js-motionSeal[data-draw="view"]',
    svg: 'svg',
    ring: '.js-sealRing',
    wordmark: '.js-sealWordmark',
    arcs: '.js-sealArcs',
    tree: '.js-sealTree',
    leaf: '.js-sealLeaves > path',
    initials: '.js-sealInitials',
    initial: '.js-sealInitials > path',
});

const FRAMES = 81;
const DRAW_ON_VIEW_MS = 4000;

// Finish within 55% of this clock so the landing hero can start its next scene.
const CUE = Object.freeze({
    tree: {from: 0, to: 13},
    ring: {from: 0, to: 31},
    wordmark: {from: 6, to: 25, arc: 51.5, startAngle: -91},
    arcs: {from: 13, to: 30},
    leaves: {from: 6, spread: 13, span: 15},
    initials: {from: 23, step: 2, span: 14},
});

/** The seal art, in the units of its 240 x 240 view box. */
const ART = Object.freeze({
    size: 240,
    center: 120,
    /** A pie slice of this radius reaches past the corners of the view box. */
    cover: 170,
    /** Where the trunk meets the ground line: the initials wait above it, out of sight. */
    groundLine: 184.3,
});

const EASE = Object.freeze({
    draw: EASING_CURVES['ease-in-out-cubic'],
    wipe: EASING_CURVES['ease-in-out-quad'],
    grow: EASING_CURVES['ease-out-quint'],
    unfold: EASING_CURVES['ease-out-back'],
    arcs: EASING_CURVES['ease-out-cubic'],
});

/** Hold the first keyframe through the delay; the resting art takes over at the end. */
const FILL = 'backwards';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

let clipId = 0;

/**
 * Put a clip over one group.
 * @param {SVGSVGElement} svg
 * @param {SVGElement} group The part the clip covers.
 * @param {SVGElement} shape The one shape inside the clip: what it covers shows.
 * @returns {() => void} Takes the clip off again and leaves the group as plain art.
 */
function clip(svg, group, shape) {
    clipId += 1;
    const box = document.createElementNS(SVG_NAMESPACE, 'clipPath');
    box.id = `sealClip-${clipId}`;
    box.setAttribute('clipPathUnits', 'userSpaceOnUse');
    box.append(shape);

    let defs = svg.querySelector('defs');
    if (defs === null) {
        defs = document.createElementNS(SVG_NAMESPACE, 'defs');
        svg.prepend(defs);
    }
    defs.append(box);
    group.setAttribute('clip-path', `url(#${box.id})`);

    return () => {
        group.removeAttribute('clip-path');
        box.remove();
        if (defs.childElementCount === 0) {
            defs.remove();
        }
    };
}

/**
 * A point on a circle around the seal's center.
 * @param {number} angle Degrees from the top, clockwise.
 * @param {number} radius
 * @returns {string} The point as path data.
 */
function pointAt(angle, radius) {
    const radians = (angle * Math.PI) / 180;

    return `${ART.center + radius * Math.sin(radians)} ${ART.center - radius * Math.cos(radians)}`;
}

/**
 * A pie slice from the center, wide enough to cover the view box: an empty path when it is closed,
 * the whole box once it has gone all the way round.
 * @param {number} startAngle Degrees from the top, where the slice begins.
 * @param {number} sweep Degrees the slice covers, clockwise.
 * @returns {string} Path data.
 */
function sectorPath(startAngle, sweep) {
    if (sweep <= 0) {
        return '';
    }
    if (sweep >= 360) {
        return `M0 0H${ART.size}V${ART.size}H0Z`;
    }
    const radius = ART.cover;

    return `M${ART.center} ${ART.center}L${pointAt(startAngle, radius)}A${radius} ${radius} 0 ${sweep > 180 ? 1 : 0} 1 ${pointAt(startAngle + sweep, radius)}Z`;
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @returns {SVGRectElement}
 */
function rectShape(x, y, width, height) {
    const rect = document.createElementNS(SVG_NAMESPACE, 'rect');
    rect.setAttribute('x', String(x));
    rect.setAttribute('y', String(y));
    rect.setAttribute('width', String(width));
    rect.setAttribute('height', String(height));

    return rect;
}

/**
 * Distance from the foot of the trunk, so the leaves open from the inside outward.
 * @param {DOMRect} box
 * @returns {number}
 */
function reachFromTrunk(box) {
    const x = box.x + box.width / 2 - ART.center;
    const y = box.y + box.height / 2 - ART.groundLine;

    return Math.hypot(x, y);
}

/**
 * Draw the seal inside the given host.
 * @param {HTMLElement} host The element that holds the inlined seal.
 * @param {number} duration Milliseconds the whole draw takes.
 * @returns {Animation[]} Every animation of the draw; finishing them leaves the resting seal.
 */
export function drawSeal(host, duration) {
    const svg = host.querySelector(SELECTOR.svg);
    if (!(svg instanceof SVGSVGElement)) {
        return [];
    }
    // Read the SVG geometry before clip paths and animations invalidate its layout.
    const arcs = svg.querySelector(SELECTOR.arcs);
    const arcBox = arcs instanceof SVGGraphicsElement ? arcs.getBBox() : null;
    const leaves = [...svg.querySelectorAll(SELECTOR.leaf)]
        .map((leaf) => {
            const box = leaf.getBBox();

            return {leaf, box, reach: reachFromTrunk(box)};
        })
        .sort((one, other) => one.reach - other.reach);
    const tree = svg.querySelector(SELECTOR.tree);
    const initials = svg.querySelector(SELECTOR.initials);
    const letters = initials instanceof SVGGraphicsElement ? initials.getBBox() : null;
    const orderedInitials = [...svg.querySelectorAll(SELECTOR.initial)]
        .map((initial) => ({initial, x: initial.getBBox().x}))
        .sort((one, other) => one.x - other.x);
    /**
     * @param {number} from First frame of the cue.
     * @param {number} to Last frame of the cue.
     * @returns {{delay: number, duration: number, fill: string}}
     */
    const between = (from, to) => ({delay: Math.round((from / FRAMES) * duration), duration: Math.round(((to - from) / FRAMES) * duration), fill: FILL});
    const animations = [];

    /**
     * Open a pie slice over one group, clockwise from its start angle.
     * @param {string} selector
     * @param {number} startAngle
     * @param {number} arc Degrees the slice covers when the reveal ends.
     * @param {object} options
     */
    const sweep = (selector, startAngle, arc, options) => {
        const group = svg.querySelector(selector);
        if (!(group instanceof SVGGraphicsElement)) {
            return;
        }
        const shape = document.createElementNS(SVG_NAMESPACE, 'path');
        const release = clip(svg, group, shape);
        const clock = drive(shape, options, (progress) => shape.setAttribute('d', sectorPath(startAngle, progress * arc)));
        settle(clock).then(release);
        animations.push(clock);
    };

    sweep(SELECTOR.ring, 0, 360, {...between(CUE.ring.from, CUE.ring.to), easing: EASE.draw});
    sweep(SELECTOR.wordmark, CUE.wordmark.startAngle, (CUE.wordmark.arc / 100) * 360, {...between(CUE.wordmark.from, CUE.wordmark.to), easing: EASE.wipe});

    // The side arcs come down behind a window that opens from their top edge.
    if (arcBox !== null) {
        const shape = rectShape(arcBox.x, arcBox.y, arcBox.width, 0);
        const release = clip(svg, arcs, shape);
        const clock = drive(shape, {...between(CUE.arcs.from, CUE.arcs.to), easing: EASE.arcs}, (progress) => shape.setAttribute('height', String(arcBox.height * progress)));
        settle(clock).then(release);
        animations.push(clock);
    }

    if (tree instanceof SVGGraphicsElement) {
        animations.push(tree.animate([
            {scale: '0.86 0.72', opacity: 0, transformOrigin: `${ART.center}px ${ART.groundLine}px`},
            {scale: '1', opacity: 1, transformOrigin: `${ART.center}px ${ART.groundLine}px`},
        ], {...between(CUE.tree.from, CUE.tree.to), easing: EASE.grow}));
    }

    const leafStep = leaves.length > 1 ? CUE.leaves.spread / (leaves.length - 1) : 0;
    leaves.forEach(({leaf, box}, index) => {
        // The corner nearest the trunk holds still as the leaf unfolds.
        const left = box.x + box.width / 2 < ART.center;
        const origin = `${left ? box.x + box.width : box.x}px ${box.y + box.height}px`;
        const from = CUE.leaves.from + index * leafStep;
        animations.push(leaf.animate([
            {scale: '0.15 0.35', rotate: `${left ? 28 : -28}deg`, opacity: 0, transformOrigin: origin},
            {offset: 0.18, opacity: 1},
            {scale: '1', rotate: '0deg', opacity: 1, transformOrigin: origin},
        ], {...between(from, from + CUE.leaves.span), easing: EASE.unfold}));
    });

    // The initials fall into the window under the ground line, one after the other.
    if (letters !== null) {
        const release = clip(svg, initials, rectShape(0, ART.groundLine, ART.size, ART.size - ART.groundLine));
        // Far enough that the tallest letter waits entirely above the line.
        const drop = letters.y + letters.height - ART.groundLine;
        const falls = orderedInitials
            .map(({initial}, index) => {
                const from = CUE.initials.from + index * CUE.initials.step;

                return initial.animate([{translate: `0 ${-drop}px`}, {translate: '0 0'}], {...between(from, from + CUE.initials.span), easing: EASE.unfold});
            });
        Promise.all(falls.map(settle)).then(release);
        animations.push(...falls);
    }

    return animations;
}

/**
 * @param {HTMLElement} host
 */
function initialize(host) {
    if (host.dataset.motionState !== undefined) {
        return;
    }
    host.dataset.motionState = 'idle';
    const observer = new IntersectionObserver(([entry]) => {
        if (!host.isConnected) {
            observer.disconnect();
            return;
        }
        if (!entry.isIntersecting) {
            return;
        }
        observer.disconnect();
        host.dataset.motionState = 'drawing';
        const run = createRun();
        run.add(...drawSeal(host, DRAW_ON_VIEW_MS));
        run.settled().then(() => {
            host.dataset.motionState = 'drawn';
        });
    });
    observer.observe(host);
}

/**
 * Initialize every seal that draws on view and is not initialized yet. Safe to call after content swaps.
 */
export function initializeComponent() {
    for (const host of document.querySelectorAll(SELECTOR.host)) {
        if (host instanceof HTMLElement) {
            initialize(host);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}
document.addEventListener('htmx:load', initializeComponent);
